import { isHeicFile } from "@base/lib/isHeicFile";
import { cn } from "@base/lib/utils";
import { FolderUp } from "lucide-react";
import {
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";

/**
 * `image/*` 만으로는 부족하다. 파일 선택창의 필터는 OS 가 알려준 MIME 로 동작하는데,
 * Windows 는 HEIF Image Extensions 가 없으면 `.heic` 에 MIME 매핑이 없어 파일이 아예 보이지 않는다.
 * 확장자 패턴을 함께 넣어 선택 자체가 가능하게 한다.
 * 큐의 `ACCEPTED_IMAGE_TYPES` 와 같은 집합을 유지해야 한다.
 */
const DEFAULT_ACCEPT = "image/*,.heic,.heif";

interface ImageUploadInputProps {
  /** 파일 선택 시 호출 — `useImageUploadQueue.addFiles` 를 그대로 전달 */
  onSelect: (files: FileList | File[]) => void;
  /** 다중 선택 허용 */
  multiple?: boolean;
  /** input accept 속성. 기본값 {@link DEFAULT_ACCEPT} */
  accept?: string;
  disabled?: boolean;
  className?: string;
  children?: ReactNode | ((dragging: boolean) => ReactNode);
}

export function ImageUploadInput({
  onSelect,
  multiple = true,
  accept = DEFAULT_ACCEPT,
  disabled = false,
  className,
  children,
}: ImageUploadInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        onSelect(e.target.files);
        e.target.value = "";
      }
    },
    [onSelect],
  );

  const handleClick = useCallback(() => inputRef.current?.click(), []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      if (disabled) return;

      const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/") || isHeicFile(f));
      if (files.length) onSelect(files);
    },
    [disabled, onSelect],
  );

  return (
    <button
      type="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "cursor-pointer",
        disabled && "pointer-events-none opacity-50",
        "p-4 rounded-4 border border-dashed border-gray-300 bg-white flex flex-col items-center justify-center gap-1 w-full",
        className,
      )}
    >
      <FolderUp />
      <span className="whitespace-pre-wrap text-body4">
        Browse to upload{"\n"}(Files with 'jpeg', 'png', 'webp', 'heic', 'heif' only)
      </span>
      <span className="inline-block mt-2 text-gray-500 text-caption1">Maximum file size: 20MB</span>
      <input
        id="site-picture-upload"
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        disabled={disabled}
        className="sr-only"
      />
      {typeof children === "function" ? children(dragging) : children}
    </button>
  );
}
